import { Form, Formik } from 'formik'
import React from 'react'
import * as yup from 'yup'

import CheckboxInput from '@/components/form/CheckboxInput'
import FormGroup from '@/components/form/FormGroup'
import MultiCheckboxInput from '@/components/form/MultiCheckboxInput'
import TextInput from '@/components/form/TextInput'
import ActionButton from '@/components/misc/ActionButton'
import AgreeToCompanyPrivacyPolicyLabel from '@/components/misc/AgreeToCompanyPrivacyPolicyLabel'
import { toSafeHttpUrl } from '@/lib/helpers/helperFunctions'
import { CompanyEntity } from '@/types/entities'

interface Props {
  company: Pick<CompanyEntity, 'presentationName' | 'privacyPolicyUrl'>
  initialValues?: RequestCallbackFormValues
  onSubmit: (values: RequestCallbackFormValues) => void
}

export interface RequestCallbackFormValues {
  times: string[]
  telephone: string
  agreesToPrivacyPolicy: boolean
}

const RequestReferralCallbackFormFields: Record<
  keyof RequestCallbackFormValues,
  keyof RequestCallbackFormValues
> = {
  times: 'times',
  telephone: 'telephone',
  agreesToPrivacyPolicy: 'agreesToPrivacyPolicy',
}

const RequestReferralCallbackForm: React.FC<Props> = ({
  company,
  initialValues,
  onSubmit,
}) => {
  const privacyPolicyUrl = toSafeHttpUrl(company.privacyPolicyUrl)

  const validationSchema = yup.object().shape({
    [RequestReferralCallbackFormFields.times]: yup
      .array()
      .min(1, 'Please select at least one callback time')
      .required('required'),
    [RequestReferralCallbackFormFields.telephone]: yup
      .string()
      .required('Your telephone is required'),
    [RequestReferralCallbackFormFields.agreesToPrivacyPolicy]: yup
      .boolean()
      .test(
        'privacy-policy',
        'Please confirm you have read and agree to the privacy policy',
        (value) => {
          // Require a value if the company has a privacy policy URL
          if (privacyPolicyUrl) {
            return !!value
          }

          return true
        },
      ),
  })

  return (
    <Formik
      initialValues={
        initialValues ?? {
          times: [],
          telephone: '',
          agreesToPrivacyPolicy: false,
        }
      }
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <Form noValidate>
          <>
            <p className="mb-4">
              Please provide the telephone number you would like{' '}
              {company.presentationName} to contact you on.
            </p>

            <FormGroup>
              <TextInput
                label="Telephone *"
                placeholder="Telephone"
                name={RequestReferralCallbackFormFields.telephone}
                autoFocus
              />
            </FormGroup>

            <FormGroup>
              <MultiCheckboxInput
                label="Preferred callback time"
                name={RequestReferralCallbackFormFields.times}
                compact
                options={[
                  { label: 'Morning', value: 'morning' },
                  { label: 'Afternoon', value: 'afternoon' },
                  { label: 'Evening', value: 'evening' },
                ]}
              />
            </FormGroup>

            {privacyPolicyUrl && (
              <FormGroup>
                <CheckboxInput
                  label={
                    <AgreeToCompanyPrivacyPolicyLabel
                      company={{
                        privacyPolicyUrl,
                        presentationName: company.presentationName,
                      }}
                    />
                  }
                  labelClassName="text-muted-foreground text-xs mt-0.25"
                  name={RequestReferralCallbackFormFields.agreesToPrivacyPolicy}
                />
              </FormGroup>
            )}

            <div className="mt-6 flex justify-end">
              <ActionButton
                size="lg"
                type="submit"
                isProcessing={isSubmitting}
                className="px-6"
              >
                Send request
              </ActionButton>
            </div>
          </>
        </Form>
      )}
    </Formik>
  )
}

export default RequestReferralCallbackForm
